import * as React from 'react'
import styled from 'styled-components'

import { Picture, breakpoint, color, stickers, styles as S } from '@bob/design-system'
import * as entity from '@boutique/entities'

type HeadProps = {
  article: entity.article.Article
  readTime: null | string
}

export function Head({ article, readTime }: HeadProps): React.ReactElement {
  return (
    <Section>
      <Block>
        {/* RESPONSIVE: Title3 on mobile --- Title4 on desktop  */}
        <S.heading.Title3 as="h1">{article.title}</S.heading.Title3>
        <S.heading.Title4 as="h1">{article.title}</S.heading.Title4>
        {article.forceUpdatedAt ? (
          <PublishedAt>
            Mis à jour le{' '}
            {new Date(article.forceUpdatedAt ?? '').toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            })}
          </PublishedAt>
        ) : (
          <PublishedAt>
            publié le{' '}
            {new Date(article.firstPublishedAt ?? '').toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'numeric',
              day: 'numeric',
            })}
          </PublishedAt>
        )}
        <ReadTime>
          <Line height="22" width="2">
            <line stroke="black" x1="0" y1="-1" x2="0" y2="21" strokeWidth="2px" />
          </Line>
          temps de lecture: {readTime && <span>{readTime} min</span>}
        </ReadTime>
      </Block>
      <S.heading.TitleCard>{article.hat}</S.heading.TitleCard>
      <Square>
        <ResponsiveSticker>
          <SectionSticker section={article.section} />
        </ResponsiveSticker>
        <Picture src={article.image.src} />
        {article.image.credit && <S.copy.TextInfo dangerouslySetInnerHTML={{ __html: article.image.credit }} />}
      </Square>
    </Section>
  )
}

function SectionSticker({
  section,
}: {
  section: 'Actus' | 'Guide de la consommation responsable' | 'Histoires de créateurs et créatrices' | null
}): React.ReactElement {
  switch (section) {
    case 'Actus':
      return <stickers.blog.News />
    case 'Guide de la consommation responsable':
      return <stickers.blog.Guide />
    case 'Histoires de créateurs et créatrices':
      return <stickers.blog.Creator />
    default:
      return <></>
  }
}

const Section = styled.section`
  display: grid;
  position: relative;

  @media screen and (${breakpoint.TABLET}) {
    display: flex;
    flex-direction: column;
    margin-bottom: 80px;
    min-height: 415px;
    width: 100%;
  }

  @media screen and (${breakpoint.LAPTOP}) {
    height: 0;
    min-height: 0px;
    padding-bottom: 50%;
  }

  ${S.heading.Title3} {
    margin-top: 25px;
    margin-bottom: 10px;

    @media screen and (${breakpoint.TABLET}) {
      display: none;
    }
  }

  ${S.heading.Title4} {
    @media not screen and (${breakpoint.TABLET}) {
      display: none;
    }
    @media screen and (${breakpoint.TABLET}) {
      margin-bottom: 10px;
      padding-right: 80px;
    }
  }

  ${S.heading.TitleCard} {
    order: 1;
    font-size: 20px;
    line-height: 23.6px;

    @media screen and (${breakpoint.TABLET}) {
      order: auto;
      width: 50%;
      margin-bottom: 0px;
      margin-top: 26px;
      padding-right: 80px;
    }
  }

  ${Picture} {
    z-index: 0;
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    border-radius: 12px;
    overflow: hidden;
  }
`
const Block = styled.div`
  position: relative;

  @media screen and (${breakpoint.TABLET}) {
    width: 50%;
  }
`

const Square = styled.div`
  position: relative;
  height: 0;
  width: 100%;
  padding-bottom: 100%;

  margin: 30px 0px 45px;

  @media screen and (${breakpoint.TABLET}) {
    margin: 0px;
    padding-bottom: 50%;
    position: absolute;
    right: 0;
    top: 0;
    width: 50%;
  }

  ${S.copy.TextInfo} {
    left: 0%;
    position: absolute;
    top: 102%;
    width: 100%;

    @media screen and (${breakpoint.TABLET}) {
      top: 101%;
      text-indent: 14%;
      text-align: right;
    }
  }
`

const Line = styled.svg`
  display: none;

  @media screen and (${breakpoint.TABLET}) {
    display: inline;
    margin: 0px 8px;
  }
`

const ResponsiveSticker = styled.div`
  z-index: 20;
  position: absolute;
  top: 0px;
  right: 5%;

  display: flex;
  align-items: center;
  justify-content: center;

  height: 0px;
  width: 33%;

  padding-bottom: 33%;

  @media screen and (${breakpoint.TABLET}) {
    right: initial;
    top: initial;
    bottom: -21%;
    left: -15%;
  }

  ${stickers.Sticker} {
    position: absolute;
    height: 100%;
    width: 100%;
  }
`

const ReadTime = styled.div`
  @media screen and (${breakpoint.TABLET}) {
    color: ${color.GRAY_2};
    display: inline;
  }
`

const PublishedAt = styled.time`
  margin-bottom: 2px;
`
